import { FlagOutlined } from '@ant-design/icons'
import { Alert, Button, Form, Modal, Radio, notification } from 'antd'
import { useForm } from 'antd/es/form/Form'
import dayjs from 'dayjs'
import { FC, useEffect, useState } from 'react'
import useErrorModal from '../../../../../hooks/api/use-error-modal'
import { buildPrintedFormSetSnapshot } from '../../../../../helpers/forms/build-printed-form-set-snapshot'
import { userStorage } from '../../../../../services/user-storage'
import { useUpdatePrintJobExecutorMutation } from '../../../../../store/api/print-job-executors-api'
import { useUpdatePrintJobMutation } from '../../../../../store/api/print-jobs-api'
import {
    printQueueEntriesApi,
    useDeletePrintQueueEntryMutation,
} from '../../../../../store/api/print-queue-entries-api'
import { useUpdateShipmentMutation } from '../../../../../store/api/shipments-api'
import { useAppDispatch } from '../../../../../store/store'
import { JobStatuses } from '../../../../../types/api/print-job'
import { Reasons } from '../../../../../types/api/print-job-executor'
import PrintQueueEntry from '../../../../../types/api/print-queue-entry'
import { getCurrentUserActiveExecutor, isCurrentUserActiveExecutor } from '../helpers/print-job-helpers'

interface FinishPrintProps {
    printQueueEntry: PrintQueueEntry
}

const FinishPrint: FC<FinishPrintProps> = ({ printQueueEntry }) => {
    const [open, setOpen] = useState<boolean>(false)
    const [form] = useForm<{ status: Reasons }>()
    const [updatePrintJobExecutor, updatePrintJobExecutorState] =
        useUpdatePrintJobExecutorMutation()
    const [updatePrintJob, updatePrintJobState] = useUpdatePrintJobMutation()
    const [deletePrintQueueEntry, deletePrintQueueEntryState] = useDeletePrintQueueEntryMutation()
    const [updateShipment, updateShipmentState] = useUpdateShipmentMutation()
    const dispatch = useAppDispatch()
    const currentUserDocumentId = userStorage.get()?.documentId
    const printJob = printQueueEntry.print_job
    const disabled = !isCurrentUserActiveExecutor(printJob, currentUserDocumentId)
    const isLoading =
        updatePrintJobExecutorState.isLoading ||
        updatePrintJobState.isLoading ||
        deletePrintQueueEntryState.isLoading ||
        updateShipmentState.isLoading

    const onFinishPrint = () => {
        setOpen(true)
    }

    const onUpdatePrintJobExecutor = async (reason: Reasons) => {
        const executor = getCurrentUserActiveExecutor(printJob, currentUserDocumentId)
        if (!executor) return

        return updatePrintJobExecutor({
            docId: executor.documentId,
            body: {
                data: {
                    end: dayjs().toISOString(),
                    reason,
                },
            },
        })
    }

    const closeShift = async () => {
        const result = await onUpdatePrintJobExecutor(Reasons.ClosingShift)
        if (result?.data) {
            notification.success({ message: 'Смена закрыта' })
        }
    }

    const savePrintedFormSetSnapshot = async () => {
        const shipment = printJob?.shipment
        if (!shipment?.documentId || !shipment.form_set || shipment.printedFormSetSnapshot) {
            return
        }

        await updateShipment({
            docId: shipment.documentId,
            body: {
                data: {
                    printedFormSetSnapshot: buildPrintedFormSetSnapshot(shipment.form_set),
                },
            },
        }).unwrap()
    }

    const done = async () => {
        const result = await onUpdatePrintJobExecutor(Reasons.Done)
        if (result?.data && printJob?.documentId) {
            await updatePrintJob({
                docId: printJob.documentId,
                body: {
                    data: {
                        jobStatus: JobStatuses.Completed,
                    },
                },
            }).unwrap()
            await savePrintedFormSetSnapshot()
            await deletePrintQueueEntry(printQueueEntry.documentId)
        }
    }

    const onConfirm = async () => {
        try {
            await form.validateFields()
            const values = form.getFieldsValue()

            if (values.status === Reasons.ClosingShift) {
                await closeShift()
            } else if (values.status === Reasons.Done) {
                await done()
            }
        } catch (e) {
            console.error(e)
        }
    }

    useEffect(() => {
        if (deletePrintQueueEntryState.isSuccess || updatePrintJobExecutorState.isSuccess) {
            dispatch(printQueueEntriesApi.util.invalidateTags(['PrintQueueEntries']))
            setOpen(false)
        }
    }, [deletePrintQueueEntryState.isSuccess, updatePrintJobExecutorState.isSuccess, dispatch])

    useErrorModal(updatePrintJobExecutorState, 'Ошибка обновления исполнителя задания')
    useErrorModal(updatePrintJobState, 'Ошибка обновления задания печати')
    useErrorModal(deletePrintQueueEntryState, 'Ошибка удаления задания из очереди')
    useErrorModal(updateShipmentState, 'Ошибка сохранения слепка набора форм')

    return (
        <>
            <Button
                icon={<FlagOutlined />}
                onClick={onFinishPrint}
                disabled={disabled}
                loading={isLoading}
            >
                Завершить
            </Button>
            <Modal
                open={open}
                onOk={onConfirm}
                confirmLoading={isLoading}
                onCancel={() => setOpen(false)}
            >
                <Form form={form}>
                    <Form.Item
                        name={'status'}
                        rules={[
                            {
                                required: true,
                                message: 'Пожалуйста, укажите причину завершения печати',
                            },
                        ]}
                    >
                        <Radio.Group
                            optionType={'button'}
                            options={[
                                { value: Reasons.Done, label: Reasons.Done },
                                { value: Reasons.ClosingShift, label: Reasons.ClosingShift },
                            ]}
                        />
                    </Form.Item>
                    <Alert
                        type={'info'}
                        message={
                            <>
                                Не забудьте отправить фотоотчет с номером задания{' '}
                                <b>{printQueueEntry.print_job?.id}</b> в телеграм
                            </>
                        }
                    />
                </Form>
            </Modal>
        </>
    )
}

export default FinishPrint
